import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { t } from '@/i18n/ro';

// URL-ul fix al instanței OpenProject (relativ, prin proxy nginx)
export const OPENPROJECT_INSTANCE_URL = '';

// Configurare client API
let apiClient: AxiosInstance | null = null;
let baseUrl: string = '';
let authCredentials: string = '';

export interface ApiError {
  message: string;
  status?: number;
  details?: unknown;
}

// Inițializare client API
export function initializeApi(instanceUrl: string, credentials: string): void {
  baseUrl = instanceUrl.replace(/\/$/, '');
  authCredentials = credentials;

  apiClient = axios.create({
    baseURL: `${baseUrl}/api/v3`,
    headers: {
      'Authorization': `Basic ${credentials}`,
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  });

  // Interceptor pentru erori
  apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const apiError = handleApiError(error);
      return Promise.reject(apiError);
    }
  );
}

// Verificare dacă API-ul este inițializat
export function isApiInitialized(): boolean {
  return apiClient !== null;
}

// Obținere client API
export function getApiClient(): AxiosInstance {
  if (!apiClient) {
    throw new Error(t('errors.unauthorized'));
  }
  return apiClient;
}

// Obținere URL de bază
export function getBaseUrl(): string {
  return baseUrl;
}

// Obținere credențiale
export function getAuthCredentials(): string {
  return authCredentials;
}

// Resetare client API (la logout)
export function resetApi(): void {
  apiClient = null;
  baseUrl = '';
  authCredentials = '';
}

// Handler pentru erori API
function handleApiError(error: AxiosError): ApiError {
  if (error.response) {
    const status = error.response.status;
    const data = error.response.data as Record<string, unknown>;

    switch (status) {
      case 401:
        return {
          message: t('errors.unauthorized'),
          status,
          details: data,
        };
      case 403:
        return {
          message: t('errors.forbidden'),
          status,
          details: data,
        };
      case 404:
        return {
          message: t('errors.notFound'),
          status,
          details: data,
        };
      case 422:
        return {
          message: t('errors.validation'),
          status,
          details: data,
        };
      case 500:
      case 502:
      case 503:
        return {
          message: t('errors.server'),
          status,
          details: data,
        };
      default:
        return {
          message: extractErrorMessage(data) || t('errors.general'),
          status,
          details: data,
        };
    }
  } else if (error.request) {
    if (error.code === 'ECONNABORTED') {
      return {
        message: t('errors.timeout'),
      };
    }
    return {
      message: t('errors.network'),
    };
  } else {
    return {
      message: error.message || t('errors.general'),
    };
  }
}

// Extragere mesaj de eroare din răspunsul API
function extractErrorMessage(data: Record<string, unknown>): string | null {
  if (data?.message && typeof data.message === 'string') {
    return data.message;
  }
  if (data?.errorIdentifier && typeof data.errorIdentifier === 'string') {
    return data.errorIdentifier;
  }
  if (data?._embedded && typeof data._embedded === 'object') {
    const embedded = data._embedded as Record<string, unknown>;
    if (embedded.errors && Array.isArray(embedded.errors)) {
      const messages = embedded.errors
        .map((e: Record<string, unknown>) => e.message)
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join(', ');
      }
    }
  }
  return null;
}

// Mapare endpoint-uri OpenProject API -> Auth Data API (GET)
function mapToAuthDataEndpoint(endpoint: string): string | null {
  // Projects
  if (endpoint === '/projects') return '/auth/data/projects';
  if (endpoint.match(/^\/projects\/\d+$/)) return endpoint.replace('/projects/', '/auth/data/projects/');
  if (endpoint.match(/^\/projects\/\d+\/versions$/)) return endpoint.replace('/projects/', '/auth/data/projects/');
  if (endpoint.match(/^\/projects\/\d+\/types$/)) return endpoint.replace('/projects/', '/auth/data/projects/');

  // Work packages
  if (endpoint === '/work_packages') return '/auth/data/work-packages';
  if (endpoint.match(/^\/work_packages\/\d+$/)) return endpoint.replace('/work_packages/', '/auth/data/work-packages/');
  if (endpoint.match(/^\/work_packages\/\d+\/attachments$/)) return endpoint.replace('/work_packages/', '/auth/data/work-packages/');
  if (endpoint.match(/^\/work_packages\/\d+\/relations$/)) return endpoint.replace('/work_packages/', '/auth/data/work-packages/');
  // Project work packages
  if (endpoint.match(/^\/projects\/\d+\/work_packages$/)) {
    const projectId = endpoint.match(/^\/projects\/(\d+)\/work_packages$/)?.[1];
    return `/auth/data/projects/${projectId}/work-packages`;
  }

  // Metadata
  if (endpoint === '/types') return '/auth/data/types';
  if (endpoint === '/statuses') return '/auth/data/statuses';
  if (endpoint === '/priorities') return '/auth/data/priorities';

  // Users
  if (endpoint === '/users/me') return '/auth/data/users/me';
  if (endpoint === '/users') return '/auth/data/users';

  // Memberships
  if (endpoint === '/memberships') return '/auth/data/memberships';

  // Time entries
  if (endpoint === '/time_entries') return '/auth/data/time-entries';
  if (endpoint === '/time_entries/activities') return '/auth/data/time-entries/activities';

  return null;
}

// Mapare endpoint-uri pentru POST/PATCH
function mapToAuthDataEndpointPost(endpoint: string): string | null {
  // Create work package în proiect
  if (endpoint.match(/^\/projects\/\d+\/work_packages$/)) {
    const projectId = endpoint.match(/^\/projects\/(\d+)\/work_packages$/)?.[1];
    return `/auth/data/projects/${projectId}/work-packages`;
  }
  // Update work package
  if (endpoint.match(/^\/work_packages\/\d+$/)) {
    return endpoint.replace('/work_packages/', '/auth/data/work-packages/');
  }
  // Projects form (pentru verificare permisiuni)
  if (endpoint === '/projects/form') return '/auth/data/projects/form';
  if (endpoint.match(/^\/projects\/\d+\/work_packages\/form$/)) {
    const projectId = endpoint.match(/^\/projects\/(\d+)\/work_packages\/form$/)?.[1];
    return `/auth/data/projects/${projectId}/work-packages/form`;
  }
  // Time entries
  if (endpoint === '/time_entries') return '/auth/data/time-entries';

  return null;
}

// Funcție helper pentru request-uri GET
export async function apiGet<T>(
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<T> {
  // Verificăm dacă avem un endpoint auth data direct
  if (endpoint.startsWith('/auth/')) {
    const response = await axios.get<T>(`${baseUrl}${endpoint}`, {
      ...config,
      headers: {
        ...config?.headers,
        'Authorization': `Basic ${authCredentials}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  // Încercăm să mapăm la auth data endpoint
  const authEndpoint = mapToAuthDataEndpoint(endpoint);
  if (authEndpoint) {
    try {
      const response = await axios.get<T>(`${baseUrl}${authEndpoint}`, {
        ...config,
        headers: {
          ...config?.headers,
          'Authorization': `Basic ${authCredentials}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (err) {
      console.warn(`Auth data endpoint failed for ${authEndpoint}, falling back to API`);
    }
  }

  // Fallback la API-ul standard
  const client = getApiClient();
  const response = await client.get<T>(endpoint, config);
  return response.data;
}

// Funcție helper pentru request-uri GET direct la auth/data endpoints
export async function authDataGet<T>(
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const authEndpoint = `/auth/data${endpoint}`;
  const response = await axios.get<T>(`${baseUrl}${authEndpoint}`, {
    ...config,
    headers: {
      ...config?.headers,
      'Authorization': `Basic ${authCredentials}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
}

// Funcție helper pentru request-uri POST
export async function apiPost<T>(
  endpoint: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  // Verificăm dacă avem un endpoint auth data direct
  if (endpoint.startsWith('/auth/')) {
    const response = await axios.post<T>(`${baseUrl}${endpoint}`, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Authorization': `Basic ${authCredentials}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  // Încercăm să mapăm la auth data endpoint
  const authEndpoint = mapToAuthDataEndpointPost(endpoint);
  if (authEndpoint) {
    try {
      const response = await axios.post<T>(`${baseUrl}${authEndpoint}`, data, {
        ...config,
        headers: {
          ...config?.headers,
          'Authorization': `Basic ${authCredentials}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (err) {
      console.warn(`Auth data endpoint failed for POST ${authEndpoint}, falling back to API`);
    }
  }

  // Fallback la API-ul standard
  const client = getApiClient();
  const response = await client.post<T>(endpoint, data, config);
  return response.data;
}

// Funcție helper pentru request-uri PATCH
export async function apiPatch<T>(
  endpoint: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  // Verificăm dacă avem un endpoint auth data direct
  if (endpoint.startsWith('/auth/')) {
    const response = await axios.patch<T>(`${baseUrl}${endpoint}`, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Authorization': `Basic ${authCredentials}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  // Încercăm să mapăm la auth data endpoint
  const authEndpoint = mapToAuthDataEndpointPost(endpoint);
  if (authEndpoint) {
    try {
      const response = await axios.patch<T>(`${baseUrl}${authEndpoint}`, data, {
        ...config,
        headers: {
          ...config?.headers,
          'Authorization': `Basic ${authCredentials}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (err) {
      console.warn(`Auth data endpoint failed for PATCH ${authEndpoint}, falling back to API`);
    }
  }

  // Fallback la API-ul standard
  const client = getApiClient();
  const response = await client.patch<T>(endpoint, data, config);
  return response.data;
}

// Funcție helper pentru request-uri DELETE
export async function apiDelete<T>(
  endpoint: string,
  config?: AxiosRequestConfig
): Promise<T> {
  // Verificăm dacă avem un endpoint auth data direct
  if (endpoint.startsWith('/auth/')) {
    const response = await axios.delete<T>(`${baseUrl}${endpoint}`, {
      ...config,
      headers: {
        ...config?.headers,
        'Authorization': `Basic ${authCredentials}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  const client = getApiClient();
  const response = await client.delete<T>(endpoint, config);
  return response.data;
}

// Funcție helper pentru upload fișiere
export async function apiUpload<T>(
  endpoint: string,
  file: File,
  metadata?: Record<string, unknown>
): Promise<T> {
  // Creăm FormData pentru upload
  const formData = new FormData();
  formData.append('file', file);

  if (metadata) {
    formData.append('metadata', JSON.stringify(metadata));
  }

  // Dacă endpoint-ul e auth data, folosim direct baseUrl (nu /api/v3)
  if (endpoint.startsWith('/auth/')) {
    const response = await axios.post<T>(`${baseUrl}${endpoint}`, formData, {
      headers: {
        'Authorization': `Basic ${authCredentials}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  const client = getApiClient();
  const response = await client.post<T>(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

// Funcție pentru a construi query params pentru filtrare
export function buildFilterParams(filters: Record<string, unknown>): string {
  const filterArray: string[] = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        filterArray.push(`${key}=${JSON.stringify({ operator: '=', values: value })}`);
      } else {
        filterArray.push(`${key}=${JSON.stringify({ operator: '=', values: [value] })}`);
      }
    }
  });

  return filterArray.length > 0 ? `filters=${encodeURIComponent(`[${filterArray.join(',')}]`)}` : '';
}

// Funcție pentru a obține ID din HAL link
export function extractIdFromLink(link: { href: string } | null | undefined): number | null {
  if (!link?.href) return null;
  const match = link.href.match(/\/(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}
